import type {
  TutorialStateMachine as ITutorialStateMachine,
  TutorialState,
  StepStatus,
  StepTransitionResult,
  TutorialStep,
} from '../types';

/**
 * Finite State Machine for Tutorial Flow Control
 * Enforces valid state transitions and prevents invalid states
 */
export class TutorialStateMachine implements ITutorialStateMachine {
  private state: TutorialState;
  private steps: TutorialStep[];
  private listeners: Array<(state: TutorialState) => void> = [];

  constructor(steps: TutorialStep[]) {
    this.steps = steps;
    this.state = {
      status: 'idle',
      currentStepIndex: 0,
      stepStatuses: steps.map(() => 'pending' as StepStatus),
      startedAt: undefined,
      completedAt: undefined,
    };
  }

  getCurrentState(): TutorialState {
    return { ...this.state };
  }

  start(): StepTransitionResult {
    if (this.state.status !== 'idle') {
      return {
        success: false,
        error: new Error(`Cannot start tutorial from status: ${this.state.status}`),
      };
    }

    this.state.status = 'running';
    this.state.startedAt = new Date();
    this.state.stepStatuses[0] = 'active';
    this.notifyListeners();

    return { success: true, nextStepIndex: 0 };
  }

  nextStep(): StepTransitionResult {
    if (this.state.status !== 'running') {
      return {
        success: false,
        error: new Error(`Cannot advance step from status: ${this.state.status}`),
      };
    }

    const currentStep = this.state.currentStepIndex;

    // Mark current step as completed
    this.state.stepStatuses[currentStep] = 'completed';

    // Check if this was the last step
    if (currentStep >= this.steps.length - 1) {
      return this.complete();
    }

    // Move to next step
    const nextIndex = currentStep + 1;
    this.state.currentStepIndex = nextIndex;
    this.state.stepStatuses[nextIndex] = 'active';
    this.notifyListeners();

    return { success: true, nextStepIndex: nextIndex };
  }

  previousStep(): StepTransitionResult {
    if (this.state.status !== 'running') {
      return {
        success: false,
        error: new Error(`Cannot go back from status: ${this.state.status}`),
      };
    }

    if (this.state.currentStepIndex === 0) {
      return {
        success: false,
        error: new Error('Already at first step'),
      };
    }

    const currentStep = this.state.currentStepIndex;

    // Mark current step as pending again
    this.state.stepStatuses[currentStep] = 'pending';

    // Move to previous step
    const prevIndex = currentStep - 1;
    this.state.currentStepIndex = prevIndex;
    this.state.stepStatuses[prevIndex] = 'active';
    this.notifyListeners();

    return { success: true, nextStepIndex: prevIndex };
  }

  skipStep(): StepTransitionResult {
    if (this.state.status !== 'running') {
      return {
        success: false,
        error: new Error(`Cannot skip step from status: ${this.state.status}`),
      };
    }

    const currentStep = this.state.currentStepIndex;

    // Mark current step as skipped
    this.state.stepStatuses[currentStep] = 'skipped';

    // Check if this was the last step
    if (currentStep >= this.steps.length - 1) {
      return this.complete();
    }

    // Move to next step
    const nextIndex = currentStep + 1;
    this.state.currentStepIndex = nextIndex;
    this.state.stepStatuses[nextIndex] = 'active';
    this.notifyListeners();

    return { success: true, nextStepIndex: nextIndex };
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  skipTutorial(_neverShowAgain: boolean): StepTransitionResult {
    if (this.state.status === 'completed' || this.state.status === 'skipped') {
      return {
        success: false,
        error: new Error('Tutorial already finished'),
      };
    }

    this.state.status = 'skipped';
    this.state.completedAt = new Date();

    // Mark remaining steps as skipped (including current active step)
    for (let i = this.state.currentStepIndex; i < this.steps.length; i++) {
      if (this.state.stepStatuses[i] !== 'completed') {
        this.state.stepStatuses[i] = 'skipped';
      }
    }

    this.notifyListeners();

    return { success: true };
  }

  complete(): StepTransitionResult {
    if (this.state.status === 'completed') {
      return {
        success: false,
        error: new Error('Tutorial already completed'),
      };
    }

    this.state.status = 'completed';
    this.state.completedAt = new Date();
    this.notifyListeners();

    return { success: true };
  }

  pause(): void {
    if (this.state.status === 'running') {
      this.state.status = 'paused';
      this.notifyListeners();
    }
  }

  resume(): void {
    if (this.state.status === 'paused') {
      this.state.status = 'running';
      this.notifyListeners();
    }
  }

  reset(): void {
    this.state = {
      status: 'idle',
      currentStepIndex: 0,
      stepStatuses: this.steps.map(() => 'pending'),
      startedAt: undefined,
      completedAt: undefined,
    };
    this.notifyListeners();
  }

  canTransition(toStepIndex: number): boolean {
    if (this.state.status !== 'running') {
      return false;
    }

    if (toStepIndex < 0 || toStepIndex >= this.steps.length) {
      return false;
    }

    // Can only transition to next/previous step or current step
    const diff = Math.abs(toStepIndex - this.state.currentStepIndex);
    return diff <= 1;
  }

  async validateCurrentStep(): Promise<boolean> {
    const currentStep = this.steps[this.state.currentStepIndex];
    if (!currentStep?.validateComplete) {
      return true; // No validation required
    }

    try {
      this.state.stepStatuses[this.state.currentStepIndex] = 'validating';
      this.notifyListeners();

      const result = await currentStep.validateComplete();

      if (result) {
        this.state.stepStatuses[this.state.currentStepIndex] = 'active';
      } else {
        this.state.stepStatuses[this.state.currentStepIndex] = 'error';
      }

      this.notifyListeners();
      return result;
    } catch (error) {
      this.state.stepStatuses[this.state.currentStepIndex] = 'error';
      this.state.error = error instanceof Error ? error : new Error('Validation failed');
      this.notifyListeners();
      return false;
    }
  }

  /**
   * Mark current step as errored
   */
  error(error: Error): void {
    this.state.status = 'error';
    this.state.error = error;
    this.state.stepStatuses[this.state.currentStepIndex] = 'error';
    this.notifyListeners();
  }

  /**
   * Mark current step as timed out
   */
  timeout(): void {
    this.state.status = 'timeout';
    this.state.stepStatuses[this.state.currentStepIndex] = 'timeout';
    this.notifyListeners();
  }

  /**
   * Subscribe to state changes
   */
  subscribe(listener: (state: TutorialState) => void): () => void {
    this.listeners.push(listener);

    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Clear all listeners (useful for cleanup/disposal)
   */
  clearListeners(): void {
    this.listeners = [];
  }

  private notifyListeners(): void {
    const stateCopy = this.getCurrentState();
    for (const listener of this.listeners) {
      try {
        listener(stateCopy);
      } catch (error) {
        console.error('Error in state machine listener:', error);
      }
    }
  }
}
